import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Template } from '../Quicktip/helpers/quicktipHelpers';
import BritePayments from '../Brite/BritePayments';
import PaystackPayments from '../Paystack/PaystackPayments';

const toAbsoluteUrl = (p: string) => process.env.PUBLIC_URL + p;

type PSPKey = 'balance' | 'brite' | 'zimpler' | 'mobile' | 'safaricom' |  'paystack';

interface PlayerInfo {
    id?:            string | number;
    phone?:         string;
    email?:         string | null;
    currency:       string;
    balance:        number;
    lang?:          string;
    feature_flags?: Record<string, any>;
}

interface OTPPaymentBlockProps {
    playerData:    PlayerInfo;
    styles:        Template;
    operator:      string;
    loading:       boolean;
    PSPs:          string[];
    onPay:         (psp: PSPKey) => void;
    ticketPrice?:  number;
    isNewAuth?:    boolean;
    onGuestAuth?:  (psp: string) => void;
    initAuth?:     () => void;
    onMobileAuth?: (psp: 'mobile' | 'safaricom') => void;
}

/* ─── icons ─── */
const ChevronRight = ({ color }: { color: string }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
        <path d="M6 4l4 4-4 4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const LockIcon = ({ color }: { color: string }) => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
        <rect x="3" y="7" width="10" height="8" rx="2" stroke={color} strokeWidth="1.5"/>
        <path d="M5 7V5a3 3 0 016 0v2" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const UserIcon = ({ color }: { color: string }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="8" r="4" stroke={color} strokeWidth="1.5"/>
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

/* ─── ConfirmPayModal ─── */
interface ConfirmPayModalProps {
    show:      boolean;
    onConfirm: () => void;
    onClose:   () => void;
    styles:    Template;
    amount:    number;
    currency:  string;
    psp:       'balance' | 'mobile' | 'safaricom';
    phone?:    string;
    loading:   boolean;
}
const ConfirmPayModal: FC<ConfirmPayModalProps> = ({
                                                       show, onConfirm, onClose, styles, amount, currency, psp, phone, loading,
                                                   }) => {
    const { t } = useTranslation();
    if (!show) return null;
    const accent    = styles.colors.buttonBGColor;
    const bg        = styles.colors.blockBgColor;
    const fontColor = styles.colors.fontColor;
    const btnColor  = styles.colors.buttonFontColor;
    const titleFont = styles.fonts.titleFontFamily;

    return (
        <>
            <div onClick={onClose} style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,.55)', zIndex: 1000,
                backdropFilter: 'blur(3px)',
            }} />
            <div style={{
                position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1001,
                background: bg, borderRadius: '20px 20px 0 0',
                padding: '12px 20px 40px',
                boxShadow: '0 -8px 40px rgba(0,0,0,.22)',
                animation: 'slideUp .28s cubic-bezier(.32,.72,0,1)',
            }}>
                {/* handle */}
                <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: `${fontColor}20`, margin: '0 auto 20px' }} />

                {/* icon */}
                <div style={{ textAlign: 'center', marginBottom: '12px', fontSize: '36px' }}>
                    {psp === 'balance' ? '💰' : '📱'}
                </div>

                <h3 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: 700, color: fontColor, fontFamily: titleFont, textAlign: 'center' }}>
                    {t('payment.confirmTitle', 'Confirm payment')}
                </h3>

                {/* amount pill */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '6px', margin: '16px 0',
                }}>
                    <span style={{
                        fontSize: '32px', fontWeight: 700, color: fontColor,
                        fontFamily: titleFont,
                    }}>
                        {amount.toFixed(2)}
                    </span>
                    <span style={{ fontSize: '16px', color: fontColor, opacity: .5 }}>{currency}</span>
                </div>

                <p style={{ margin: '0 0 24px', fontSize: '13px', color: fontColor, opacity: .5, textAlign: 'center', lineHeight: 1.5 }}>
                    {psp === 'balance'
                        ? t('payment.confirmBalanceDesc', 'This amount will be deducted from your wallet balance.')
                        : (<>{t('payment.confirmMobileDesc', 'You will receive a prompt on your phone to complete the payment.')}
                            {phone && (
                                <><br/><strong style={{ opacity: 1, color: fontColor }}>{phone}</strong></>
                            )}</>)}
                </p>

                {/* CTA */}
                <button
                    type="button"
                    disabled={loading}
                    onClick={onConfirm}
                    style={{
                        width: '100%', padding: '16px', borderRadius: '12px', border: 'none',
                        background: loading ? `${accent}60` : accent,
                        color: btnColor, fontSize: '16px', fontWeight: 700,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        letterSpacing: '.01em', marginBottom: '10px',
                    }}
                >
                    {loading ? t('payment.processing') : t('payment.payNow', 'Pay now')}
                </button>

                {/* Cancel */}
                <button
                    type="button"
                    onClick={onClose}
                    style={{
                        width: '100%', padding: '13px', borderRadius: '12px',
                        border: `0.5px solid ${fontColor}25`,
                        background: 'transparent', color: fontColor,
                        fontSize: '14px', fontWeight: 500, cursor: 'pointer', opacity: .7,
                    }}
                >
                    {t('payment.cancel', 'Cancel')}
                </button>
            </div>
            <style>{`@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
        </>
    );
};

/* ─── operator logo strip ─── */
const OPERATOR_LOGOS = [
    "/assets/operators/airtel.png",
    "/assets/operators/at.png",
    "/assets/operators/celtiis.png",
    "/assets/operators/halo.png",
    "/assets/operators/mixx.png",
    "/assets/operators/momo.png",
    "/assets/operators/movitel.png",
    "/assets/operators/movo.png",
    "/assets/operators/mpesa.png",
    "/assets/operators/orangemoney.png",
    "/assets/operators/telecel.png",
    "/assets/operators/tnm.png",
    "/assets/operators/vodacom.png",
    "/assets/operators/wave.png",
    "/assets/operators/zamtel.png",
];
const PAYSATACK_LOGOS = [
    "/assets/img/paystack/mastercard_logo.webp",
    "/assets/img/paystack/visa_logo.webp",
    "/assets/img/paystack/verve_logo.webp",
    "/assets/img/paystack/opay.webp",
];
const BRITE_LOGOS = [
    "/assets/img/brite/BNP.svg",
    "/assets/img/brite/ING.svg",
    "/assets/img/brite/Deutsche_Bank.svg",
    "/assets/img/brite/Nordea.svg",
];


interface MobileMoneyProvider {
    name: string;
    logo: string;
}

const MOBILE_MONEY_OVERRIDES: Record<string, MobileMoneyProvider[]> = {
    'bengabet-zm': [
        { name: 'Airtel', logo: '/assets/operators/airtel.png' },
        { name: 'Zamtel', logo: '/assets/operators/zamtel.png' },
        { name: 'MTN',    logo: '/assets/operators/mtn.png' },
    ],
};

const getMobileMoneyProviders = (operator: string): MobileMoneyProvider[] | null =>
    MOBILE_MONEY_OVERRIDES[operator] ?? null;

const OperatorStrip: FC<{ fontColor: string }> = ({ fontColor }) => (
    <div style={{
        display:    'flex',
        alignItems: 'center',
        gap:        '6px',
        marginTop:  '6px',
        overflowX:  'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '2px',
    }}>
        {OPERATOR_LOGOS.map((src, i) => (
            <img
                key={i}
                src={toAbsoluteUrl(src)}
                alt=""
                style={{
                    width:        '24px',
                    height:       '24px',
                    objectFit:    'contain',
                    borderRadius: '6px',
                    flexShrink:   0,
                    opacity:      .85,
                }}
            />
        ))}
        <span style={{
            fontSize:    '11px',
            color:       fontColor,
            opacity:     .45,
            whiteSpace:  'nowrap',
            flexShrink:  0,
            marginLeft:  '2px',
        }}>
            +22
        </span>
    </div>
);
const BankStrip: FC<{ fontColor: string }> = ({ fontColor }) => (
    <div style={{
        display:    'flex',
        alignItems: 'center',
        gap:        '6px',
        marginTop:  '6px',
        overflowX:  'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '2px',
    }}>
        {PAYSATACK_LOGOS.map((src, i) => (
            <img
                key={i}
                src={toAbsoluteUrl(src)}
                alt=""
                style={{
                    width:        '24px',
                    height:       '24px',
                    objectFit:    'contain',
                    borderRadius: '6px',
                    flexShrink:   0,
                    opacity:      .85,
                }}
            />
        ))}
        <span style={{
            fontSize:    '11px',
            color:       fontColor,
            opacity:     .45,
            whiteSpace:  'nowrap',
            flexShrink:  0,
            marginLeft:  '2px',
        }}>
            +22
        </span>
    </div>
);
/* ─── PSPCard ─── */
interface PSPCardProps {
    logo:        React.ReactNode;
    title:       string;
    subtitle:    string;
    via?:        string;
    badge?:      string;
    extra?:      React.ReactNode;
    selected?:   boolean;
    disabled?:   boolean;
    onClick?:    () => void;
    accentColor: string;
    fontColor:   string;
    bg:          string;
}

const PSPCard: FC<PSPCardProps> = ({
                                       logo, title, subtitle, via, badge, extra,
                                       selected, disabled, onClick,
                                       accentColor, fontColor, bg,
                                   }) => (
    <button
        type="button"
        onClick={onClick ?? (() => {})}
        disabled={disabled}
        style={{
            display:      'flex',
            alignItems:   'flex-start',
            gap:          '12px',
            width:        '100%',
            padding:      '14px 14px',
            border:       selected
                ? `1.5px solid ${accentColor}`
                : `1px solid ${fontColor}22`,
            borderRadius: '14px',
            background:   selected ? `${accentColor}10` : `color-mix(in srgb, ${fontColor} 10%, ${bg})`,
            cursor:       disabled ? 'not-allowed' : 'pointer',
            opacity:      disabled ? 0.45 : 1,
            textAlign:    'left',
            transition:   'border-color .15s, background .15s',
            marginBottom: '10px',
        }}
    >
        {/* logo box */}
        <span style={{
            width:           '48px',
            height:          '48px',
            minWidth:        '48px',
            borderRadius:    '12px',
            background:      `${fontColor}0D`,
            border:          `0.5px solid ${fontColor}14`,
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            overflow:        'hidden',
            flexShrink:      0,
        }}>
            {logo}
        </span>

        {/* text */}
        <span style={{ flex: 1, minWidth: 0 }}>
            {/* title row */}
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: '15px', color: fontColor, lineHeight: 1.2 }}>
                    {title}
                </span>
                {badge && (
                    <span style={{
                        fontSize:     '10px',
                        fontWeight:   600,
                        padding:      '2px 7px',
                        borderRadius: '20px',
                        background:   `${accentColor}20`,
                        color:        accentColor,
                        lineHeight:   1.4,
                    }}>
                        {badge}
                    </span>
                )}
            </span>

            {/* subtitle */}
            <span style={{
                display:   'block',
                fontSize:  '12px',
                color:     fontColor,
                opacity:   .55,
                marginTop: '2px',
                lineHeight: 1.4,
            }}>
                {subtitle}
            </span>

            {/* extra (operator strip, etc.) */}
            {extra}

            {/* via line */}
            {via && (
                <span style={{
                    display:     'flex',
                    alignItems:  'center',
                    gap:         '4px',
                    fontSize:    '11px',
                    color:       fontColor,
                    opacity:     .35,
                    marginTop:   '5px',
                }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                              stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M8 12h8M14 9l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {via}
                </span>
            )}
        </span>

        <ChevronRight color={`${fontColor}50`} />
    </button>
);

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const OTPPaymentBlock: FC<OTPPaymentBlockProps> = ({
                                                       playerData, styles, PSPs, operator, loading, onPay, ticketPrice = 0,
                                                       isNewAuth, onGuestAuth, initAuth, onMobileAuth,
                                                   }) => {
    const { t } = useTranslation();

    const balance           = playerData?.balance  ?? 0;
    const currency          = playerData?.currency ?? '';
    const hasEnoughBalance  = balance >= ticketPrice;

    const [selected, setSelected] = useState<PSPKey | null>(
        isNewAuth && balance > 0 && hasEnoughBalance ? 'balance' : null,
    );
    const [paystackPhone, setPaystackPhone] = useState('');
    const [confirmPsp, setConfirmPsp] = useState<'balance' | 'mobile' | 'safaricom' | null>(null);

    const accent       = styles.colors.buttonBGColor;
    const bg           = styles.colors.blockBgColor;
    const fontColor    = styles.colors.fontColor;
    const btnFontColor = styles.colors.buttonFontColor;
    const titleColor   = styles.colors.fontTitleColor;

    const hasBalance = balance > 0;
    const hasPSP     = (key: string) => PSPs?.includes(key);

    const handlePay = () => {
        if (!selected || loading) return;
        if (selected === 'balance' || selected === 'mobile' || selected === 'safaricom') {
            setConfirmPsp(selected); // показуємо confirm modal
        } else {
            onPay(selected);
        }
    };




    /* ── shared card data ───────────────────────────────────────── */
    const briteCard = (
        <PSPCard
            logo={
                <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr',
                    gap: '2px', width: '38px', height: '38px',
                }}>
                    {BRITE_LOGOS.slice(0, 4).map((src, i) => (
                        <img key={i} src={toAbsoluteUrl(src)} alt=""
                             style={{ width: '17px', height: '17px', objectFit: 'contain', borderRadius: '3px' }} />
                    ))}
                </div>
            }
            title={t('payment.bankTransfer', 'Bank transfer')}
            subtitle="BNP, ING, Deutsche Bank, Nordea..."
            via="via Brite Payments"
            badge={t('payment.instant', 'Instant')}
            accentColor={accent} fontColor={fontColor} bg={bg}
        />
    );
    const paystackCard = (
        <PSPCard
            logo={
                <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr',
                    gap: '2px', width: '38px', height: '38px',
                }}>
                    {PAYSATACK_LOGOS.slice(0, 4).map((src, i) => (
                        <img key={i} src={toAbsoluteUrl(src)} alt=""
                             style={{ width: '17px', height: '17px', objectFit: 'contain', borderRadius: '3px' }} />
                    ))}
                </div>
            }
            title={t('payment.cardsWallets', 'Cards & wallets')}
            subtitle="Visa, Mastercard, bank transfer"
            via="via Paystack"
            accentColor={accent} fontColor={fontColor} bg={bg}
        />
    );

    const zimplerCard = (
        <PSPCard
            logo={<img src={toAbsoluteUrl('/assets/img/zimpler/logo-pix.webp')} alt="Zimpler"
                       style={{ width: 32, height: 32, objectFit: 'contain' }} />}
            title={t('payment.bankTransfer', 'Bank transfer')}
            subtitle="Handelsbanken, Nordea, OP Bank..."
            via={`via Zimpler · ${t('payment.swedenFinland', 'Sweden & Finland')}`}
            accentColor={accent} fontColor={fontColor} bg={bg}
        />
    );

    /* mobile-money card — per-operator config з дефолтним фолбеком.
       Немає override → рендер ідентичний попередньому (інші оператори без змін). */
    const mobileProviders = getMobileMoneyProviders(operator);
    const mobileLogos     = mobileProviders ? mobileProviders.map(p => p.logo) : OPERATOR_LOGOS.slice(0, 4);
    const mobileSubtitle  = mobileProviders ? mobileProviders.map(p => p.name).join(', ') : 'Airtel, OrangeMoney, Mpesa, Vodacom';

    const mobileCard = (
        <PSPCard
            logo={
                <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr',
                    gap: '2px', width: '38px', height: '38px',
                }}>
                    {mobileLogos.slice(0, 4).map((src, i) => (
                        <img key={i} src={toAbsoluteUrl(src)} alt=""
                             style={{ width: '17px', height: '17px', objectFit: 'contain', borderRadius: '3px' }} />
                    ))}
                </div>
            }
            title={t('payment.mobileMoney', 'Mobile money')}
            subtitle={mobileSubtitle}
            via={`via Your Mobile operator`}
            accentColor={accent} fontColor={fontColor} bg={bg}
        />
    );
    const safaricomCard = (
        <PSPCard
            logo={
                <div style={{
                    display: 'block', gridTemplateColumns: '1fr 1fr',
                    gap: '2px', width: '38px', height: '38px',
                }}>
                    <img src={toAbsoluteUrl('/assets/operators/mpesa.png')} alt="mpesa"
                         style={{width: '100%', height: '100%', objectFit: 'contain', borderRadius: '3px'}}/>
                </div>
            }
            title={t('payment.mobileMoney', 'Mpesa Money')}
            subtitle=""
            via={`via Mpesa number`}
            accentColor={accent} fontColor={fontColor} bg={bg}
        />
    );

    /* ── GUEST VIEW ─────────────────────────────────────────────── */
    if (!isNewAuth) {
        const guestPSPs = ['brite', 'paystack', 'zimpler', 'mobile', 'safaricom'].filter(hasPSP);

        /* map psp key → card with correct onClick */
        const guestCard = (psp: string) => {
            const handleClick = () => {
                if (psp === 'mobile' || psp === 'safaricom') { onMobileAuth?.(psp as 'mobile' | 'safaricom'); }
                else { onGuestAuth?.(psp); }
            };

            if (psp === 'brite') return React.cloneElement(briteCard, { onClick: handleClick } as any);
            if (psp === 'paystack') return React.cloneElement(paystackCard, { onClick: handleClick } as any);
            if (psp === 'zimpler') return React.cloneElement(zimplerCard, { onClick: handleClick } as any);
            if (psp === 'mobile') return React.cloneElement(mobileCard, { onClick: handleClick } as any);
            if (psp === 'safaricom') return React.cloneElement(safaricomCard, { onClick: handleClick } as any);
            return null;
        };

        return (
            <div style={{ backgroundColor: bg, borderRadius: '16px', overflow: 'hidden', marginBottom: '16px' }}>
                <div style={{ padding: '20px 16px 0' }}>

                    {/* hero text */}
                    <h2 style={{
                        margin: '0 0 4px',
                        fontSize: styles.fonts.titleFontSize,
                        fontWeight: 700,
                        color: fontColor,
                        fontFamily: styles.fonts.titleFontFamily,
                        textAlign: 'center',
                    }}>
                        {t('payment.playInstantly', 'Play Instantly')}
                    </h2>
                    <p style={{
                        margin: '0 0 24px',
                        fontSize: styles.fonts.mainFontSize,
                        color: fontColor, opacity: .6,
                        textAlign: 'center',
                        lineHeight: 1.5,
                    }}>
                        {t('payment.chooseHowToPay', 'Choose how you want to pay — no account needed')}
                    </p>

                    {/* NEW USERS label */}
                    <div style={{
                        display:        'flex',
                        alignItems:     'center',
                        justifyContent: 'space-between',
                        marginBottom:   '12px',
                    }}>
                        <span style={{
                            fontSize:      '11px',
                            fontWeight:    700,
                            letterSpacing: '.08em',
                            textTransform: 'uppercase',
                            color:         fontColor,
                            opacity:       .55,
                        }}>
                            {t('payment.newUsers', 'New users')}
                        </span>
                        {guestPSPs.length > 0 && (
                            <span style={{
                                fontSize:     '11px',
                                fontWeight:   500,
                                padding:      '3px 10px',
                                borderRadius: '20px',
                                border:       `0.5px solid ${fontColor}25`,
                                color:        fontColor,
                                opacity:      .55,
                            }}>
                                {guestPSPs.length} {t('payment.options', 'options')}
                            </span>
                        )}
                    </div>

                    {/* PSP list */}
                    {guestPSPs.map(psp => (
                        <React.Fragment key={psp}>
                            {guestCard(psp)}
                        </React.Fragment>
                    ))}

                    {/* OR divider */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }}>
                        <div style={{ flex: 1, height: '0.5px', background: `${fontColor}22` }} />
                        <span style={{ fontSize: '11px', color: fontColor, opacity: .4, letterSpacing: '.1em' }}>OR</span>
                        <div style={{ flex: 1, height: '0.5px', background: `${fontColor}22` }} />
                    </div>

                    {/* Existing users */}
                    <div style={{
                        border:        `0.5px solid ${fontColor}22`,
                        borderRadius:  '14px',
                        padding:       '16px',
                        textAlign:     'center',
                        marginBottom:  '20px',
                    }}>
                        <h3 style={{
                            margin: '0 0 6px',
                            fontSize: '16px',
                            fontWeight: 600,
                            color: fontColor,
                            fontFamily: styles.fonts.titleFontFamily,
                        }}>
                            {t('payment.alreadyHaveAccount', 'Already have an account?')}
                        </h3>
                        <p style={{
                            margin: '0 0 14px',
                            fontSize: styles.fonts.mainFontSize,
                            color: fontColor, opacity: .6,
                            lineHeight: 1.5,
                        }}>
                            {t('payment.loginToPayFromWallet', 'Log in to pay from your wallet balance')}
                        </p>
                        <button
                            type="button"
                            onClick={initAuth}
                            style={{
                                width:        '100%',
                                padding:      '13px',
                                borderRadius: '12px',
                                border:       `0.5px solid ${fontColor}25`,
                                background:   styles.colors.buttonBGColor,
                                color:        styles.colors.buttonFontColor,
                                fontSize:     '14px',
                                fontWeight:   500,
                                cursor:       'pointer',
                                display:      'flex',
                                alignItems:   'center',
                                justifyContent: 'center',
                                gap:          '8px',
                            }}
                        >
                            <UserIcon color={fontColor} />
                            {t('payment.logInToMyAccount', 'Log in to my account')}
                        </button>
                    </div>

                </div>

                {/* SSL note */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '5px', padding: '0 16px 16px',
                    fontSize: '11px', color: fontColor, opacity: .4,
                }}>
                    <LockIcon color={fontColor} />
                    {t('payment.securedSSL', 'Secured by SSL encryption')}
                </div>
            </div>
        );
    }

    /* ── AUTHORIZED VIEW ────────────────────────────────────────── */
    return (
        <div style={{ backgroundColor: bg, borderRadius: '16px', overflow: 'hidden', marginBottom: '16px' }}>

            {/* header */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 16px', borderBottom: `0.5px solid ${fontColor}15`,
            }}>
                <h4 style={{
                    color: titleColor,
                    fontSize: styles.fonts.titleFontSize,
                    fontFamily: styles.fonts.titleFontFamily,
                    margin: 0,
                }}>
                    {t('payment.selectMethod')}
                </h4>
            </div>

            <div style={{ padding: '16px' }}>

                {/* balance strip */}
                {balance >= 0 && (
                    <div style={{
                        background:    styles.colors.blockTitleBgColor,
                        color:         styles.colors.fontColor,
                        border:        `0.5px solid ${fontColor}20`,
                        borderRadius:  '12px',
                        marginBottom:  '20px',
                        padding:       '10px 14px',
                        textAlign:     'center',
                    }}>
                        <div style={{ fontSize: '11px',
                            opacity: .6,
                            marginBottom: '3px',
                            textTransform: 'uppercase',
                            letterSpacing: '.05em' }}>
                            {t('payment.balance')}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: '18px' }}>
                            {balance}{' '}<span style={{ fontSize: '12px', opacity: .6 }}>{currency}</span>
                        </div>
                        {/*{!hasEnoughBalance && ticketPrice > 0 && (*/}
                        {/*    <div style={{ fontSize: '11px', color: '#e74c3c', marginTop: '4px' }}>*/}
                        {/*        {t('payment.insufficientBalance', `Need ${ticketPrice} ${currency}`)}*/}
                        {/*    </div>*/}
                        {/*)}*/}
                    </div>
                )}

                {/* PSP list */}
                <div style={{ marginBottom: '16px' }}>

                    {hasBalance && (
                        <PSPCard
                            logo={<span style={{ fontSize: '22px' }}>💰</span>}
                            title={t('payment.payFromBalance')}
                            subtitle={`${balance} ${currency} ${t('payment.available')}`}
                            selected={selected === 'balance'}
                            disabled={!hasEnoughBalance}
                            onClick={() => { setSelected('balance'); setConfirmPsp('balance'); }}
                            accentColor={accent} fontColor={fontColor} bg={bg}
                        />
                    )}

                    {hasPSP('brite') && (
                        <>
                            {React.cloneElement(briteCard, {
                                selected: selected === 'brite',
                                onClick: () => setSelected('brite'),
                            } as any)}
                            {selected === 'brite' && (
                                <div style={{ marginBottom: '8px' }}>
                                    <BritePayments styles={styles} initBrite={() => onPay('brite')} />
                                </div>
                            )}
                        </>
                    )}

                    {hasPSP('zimpler') && operator !== 'bet365-demo' && (
                        React.cloneElement(zimplerCard, {
                            selected: selected === 'zimpler',
                            onClick: () => { setSelected('zimpler'); onPay('zimpler'); },
                        } as any)
                    )}

                    {hasPSP('mobile') && (
                        React.cloneElement(mobileCard, {
                            selected: selected === 'mobile',
                            onClick: () => { setSelected('mobile'); setConfirmPsp('mobile'); },
                        } as any)
                    )}
                    {hasPSP('safaricom') && (
                        React.cloneElement(safaricomCard, {
                            selected: selected === 'safaricom',
                            onClick: () => { setSelected('safaricom'); setConfirmPsp('safaricom'); },
                        } as any)
                    )}

                    {hasPSP('paystack') && operator !== 'bet365-demo' && (
                        <>
                            {React.cloneElement(paystackCard, {
                                selected: selected === 'paystack',
                                onClick: () => setSelected('paystack'),
                            } as any)}
                            {selected === 'paystack' && (
                                <div style={{ marginBottom: '8px' }}>
                                    <PaystackPayments
                                        styles={styles}
                                        initPaystack={() => onPay('paystack')}
                                        setHidePaystack={() => {}}
                                        placeholder="Phone number"
                                        handleChangeNumber={(num: string) => setPaystackPhone(num)}
                                        isAuth
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>



                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '5px', marginTop: '10px',
                    fontSize: '11px', color: fontColor, opacity: .4,
                }}>
                    <LockIcon color={fontColor} />
                    {t('payment.securedSSL', 'Secured by SSL encryption')}
                </div>

            </div>

            {/* Confirm modal — balance & mobile only */}
            <ConfirmPayModal
                show={!!confirmPsp}
                onConfirm={() => { if (confirmPsp) { onPay(confirmPsp); setConfirmPsp(null); } }}
                onClose={() => setConfirmPsp(null)}
                styles={styles}
                amount={ticketPrice}
                currency={currency}
                psp={confirmPsp ?? 'balance'}
                phone={playerData?.phone}
                loading={loading}
            />
        </div>
    );
};

export default OTPPaymentBlock;